import * as jisx0402 from '@/features/prefecture/jisx0402'
import type {
  ChatRoomFilterInput,
  ChatRoomFilterLastMessageSendAtInput,
  InputMaybe,
} from '@/types/schema.generated'
import {
  ChatMessageStatus,
  ChatRoomMemoStatus,
  ChatRoomStatus,
  Prefecture,
  UserGender,
} from '@/types/schema.generated'
import { uniqBy } from 'lodash-es'
import type { Ref } from 'vue'
import { computed, ref } from 'vue'

import { toJisx0402Code } from '@/features/prefecture/prefecture'
import type {
  Match,
  MatchUser,
  NormalMessageChatReplyMatchSetting,
  Sex,
} from '@/types/app'
import type { ChatRoomListItemFragment } from './useChatRoomFilterResult.generated'

// フィルタ条件
const returnTrue = (_: unknown) => true
const groupOr = (tests: ((match: Match) => boolean)[]) => (match: Match) =>
  tests.some(test => test(match))
const groupAnd = (tests: ((match: Match) => boolean)[]) => (match: Match) =>
  tests.every(test => test(match))

const createRoomStatusFilter = (
  selectedStatuses: InputMaybe<ChatRoomStatus[]> | undefined
) =>
  selectedStatuses == null
    ? returnTrue
    : (match: Match) => selectedStatuses.includes(match.chatRoomStatus!)

const createHasMemoFilter = (
  selectedStatuses: InputMaybe<ChatRoomMemoStatus[]> | undefined
) =>
  selectedStatuses == null ||
  !selectedStatuses.includes(ChatRoomMemoStatus.HasMemo)
    ? returnTrue
    : (match: Match) => (match.memos ?? []).length > 0

const createNoMemoFilter = (
  selectedStatuses: InputMaybe<ChatRoomMemoStatus[]> | undefined
) =>
  selectedStatuses == null ||
  !selectedStatuses.includes(ChatRoomMemoStatus.NoMemo)
    ? returnTrue
    : (match: Match) => (match.memos ?? []).length === 0

const createUnreadFilter = (
  selectedStatuses: InputMaybe<ChatMessageStatus[]> | undefined
) =>
  selectedStatuses == null ||
  !selectedStatuses.includes(ChatMessageStatus.Unread)
    ? returnTrue
    : (match: Match) => match.latestMessage?.isUnread ?? false

const createUnrepliedFilter = (
  selectedStatuses: InputMaybe<ChatMessageStatus[]> | undefined
) =>
  selectedStatuses == null ||
  !selectedStatuses.includes(ChatMessageStatus.Unreplied)
    ? returnTrue
    : (match: Match) => match.hasUnrepliedMessage ?? false

const createRepliedFilter = (
  selectedStatuses: InputMaybe<ChatMessageStatus[]> | undefined
) =>
  selectedStatuses == null ||
  !selectedStatuses.includes(ChatMessageStatus.Replied)
    ? returnTrue
    : (match: Match) => !(match.hasUnrepliedMessage ?? false)

// 最新メッセージの日時のフィルタ
const createLastMessageRangeStartFilter = (
  range: InputMaybe<ChatRoomFilterLastMessageSendAtInput> | undefined
) =>
  range?.startedAt == null
    ? returnTrue
    : (match: Match) =>
        match.latestMessage?.data != null &&
        match.latestMessage?.data.createdAt.getTime() >
          new Date(range!.startedAt!).getTime()

const createLastMessageRangeEndFilter = (
  range: InputMaybe<ChatRoomFilterLastMessageSendAtInput> | undefined
) =>
  range?.endedAt == null
    ? returnTrue
    : (match: Match) =>
        match.latestMessage?.data != null &&
        match.latestMessage?.data.createdAt.getTime() <
          new Date(range!.endedAt!).getTime()

function genderToSex(gender: UserGender): Sex {
  switch (gender) {
    case 'MALE':
      return 'male'
    case 'FEMALE':
      return 'female'
    default:
      return 'noAnswer'
  }
}

function buildMatchUser(match: ChatRoomListItemFragment): MatchUser {
  if (!match.user) {
    throw new Error('match.user is undefined')
  }

  const sex = ((): Sex => {
    return genderToSex(match.user.gender)
  })()
  const livingPrefecture = (() => {
    if (!match.user.livingPrefecture) {
      return undefined
    }
    return (
      jisx0402.findByCode(toJisx0402Code(match.user.livingPrefecture))?.name ??
      undefined
    )
  })()
  const city = match.user.address?.city || ''
  const user: MatchUser = {
    id: Number(match.user.id),
    nickname: match.user.nickname,
    email: '',
    sex,
    livingPrefecture: livingPrefecture as Prefecture,
    yearOfBirth: match.user.birthYear,
    diagnosisResult: {
      personality: {
        // NOTE: 後のストーリーで、ユーザーのアイコンが表示されなくなるので、ちゃんとした実装はせず固定の値を返す
        type: match.user.diagnosisResult?.personality.type || 'A',
        title: '',
        description: '',
      },
    },
    address: {
      prefecture: livingPrefecture,
      city: city,
    },
    karte: {
      karteUserComment: match.user.karte?.userComment,
    },
  }
  return user
}

function buildNormalMessageChatReplyMatchSetting(
  node: ChatRoomListItemFragment
): NormalMessageChatReplyMatchSetting {
  const disabledUntilString =
    node.match.normalMessageChatReplyMatchSetting.disabledUntil
  if (!disabledUntilString) {
    return {
      disabledUntil: undefined,
    }
  }

  return {
    disabledUntil: new Date(disabledUntilString),
  }
}

export const useChatRoomFilterResult = (
  chatRooms: Ref<ChatRoomListItemFragment[]>,
  filterInput: Ref<ChatRoomFilterInput> = ref({})
) => {
  const matches = computed((): Match[] => {
    const fetchResult =
      chatRooms.value.map((node): Match => ({
        id: Number(node.match.id),
        roomId: Number(node.id),
        user: buildMatchUser(node),
        planner: {
          id: Number(node.planner.id),
          name: node.planner.name,
          avatarImage: node.planner.avatar,
          chatParticipantId: node.planner.chatParticipant
            ? Number(node.planner.chatParticipant.id)
            : undefined,
        },
        latestMessage: {
          data: {
            createdAt: new Date(node.updatedAt),
          },
          isUnread: node.hasUnreadMessage,
        },
        chatRoomStatus: node.status,
        hasUnrepliedMessage: node.hasUnrepliedMessage,
        memos: node.memos,
        userLatestReadMessageGUID: undefined,
        normalMessageChatReplyMatchSetting:
          buildNormalMessageChatReplyMatchSetting(node),
      })) ?? []

    // GraphQL API から一覧を取得したあと、状態が更新された Match のために、クライアント上でもフィルタする
    // eslint-disable-next-line fp/no-mutating-methods
    const filtered = fetchResult
      .filter(createRoomStatusFilter(filterInput.value.statuses))
      .filter(
        groupOr([
          createUnreadFilter(filterInput.value.chatMessageStatuses),
          createUnrepliedFilter(filterInput.value.chatMessageStatuses),
          createRepliedFilter(filterInput.value.chatMessageStatuses),
        ])
      )
      .filter(
        groupOr([
          createHasMemoFilter(filterInput.value.memoStatuses),
          createNoMemoFilter(filterInput.value.memoStatuses),
        ])
      )
      .filter(
        groupAnd([
          createLastMessageRangeStartFilter(
            filterInput.value.lastMessageSentAt
          ),
          createLastMessageRangeEndFilter(filterInput.value.lastMessageSentAt),
        ])
      )
      .sort((a, b) => {
        const aTime = a.latestMessage?.data.createdAt.getTime() ?? 0
        const bTime = b.latestMessage?.data.createdAt.getTime() ?? 0

        const diff = bTime - aTime

        if (diff === 0) {
          return a.id - b.id
        }

        return diff
      })

    // match が被ることがあったので uniq する
    return uniqBy(filtered, 'id')
  })

  return {
    matches,
  }
}
